import React from "react";
import { PopUpConstants, shared } from "../../src/taskpane/config/constants";
import { IAuthTokenResultDto } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import authModule, { DialogMessageResponse, DialogMessageStatus, TokenResource } from "../../utilities/AuthModule";
import { logger } from "../../utilities/logger";

export function useMailSendPopUpAuth() {
    const [isMailSendTokenAvailable, setIsMailSendTokenAvailable] = React.useState<boolean>(authModule.getPersistedAuthState()?.mailSendToken?.length > 0);
    const [isMailSendTokenRequestInProgress, setIsMailSendTokenRequestInProgress] = React.useState<boolean>(false);

    const authDialogRef = React.useRef<any>();
    const popupCloseIntervalRef = React.useRef<number | null>(null);
    const pendingRequestRef = React.useRef<{
        promise: Promise<boolean>;
        resolve: (success: boolean) => void;
    } | null>(null);
    const isMountedRef = React.useRef<boolean>(true);

    const clearPopupCloseInterval = React.useCallback(() => {
        if (popupCloseIntervalRef.current) {
            window.clearInterval(popupCloseIntervalRef.current);
            popupCloseIntervalRef.current = null;
        }
    }, []);

    const closeAuthDialog = React.useCallback(() => {
        try {
            authDialogRef.current?.close();
        } catch (error) {
            logger(error, "useMailSendPopUpAuth.tsx (closeAuthDialog)");
        }
        authDialogRef.current = null;
    }, []);

    const finishTokenRequest = React.useCallback((success: boolean) => {
        clearPopupCloseInterval();
        const pendingRequest = pendingRequestRef.current;
        pendingRequestRef.current = null;

        if (isMountedRef.current) {
            setIsMailSendTokenRequestInProgress(false);
        }

        pendingRequest?.resolve(success);
    }, [clearPopupCloseInterval]);

    const requestMailSendToken = React.useCallback((): Promise<boolean> => {
        const persistedAuthState: IAuthTokenResultDto = authModule.getPersistedAuthState();
        if (persistedAuthState?.mailSendToken?.length > 0) {
            setIsMailSendTokenAvailable(true);
            return Promise.resolve(true);
        }

        if (pendingRequestRef.current) {
            return pendingRequestRef.current.promise;
        }

        let resolveRequest: (success: boolean) => void = () => undefined;
        const promise = new Promise<boolean>(resolve => {
            resolveRequest = resolve;
        });
        pendingRequestRef.current = { promise, resolve: resolveRequest };

        if (isMountedRef.current) {
            setIsMailSendTokenRequestInProgress(true);
        }

        try {
            let authState: IAuthTokenResultDto = authModule.getPersistedAuthState();

            let authUrl: URL = new URL(`${process.env.baseUri}/auth.html`);
            authUrl.searchParams.append(shared.params.app, "content_manager");
            authUrl.searchParams.append(shared.params.resource, TokenResource.MailSend.toString());
            authUrl.searchParams.append(shared.params.username, authState?.account?.username ?? "");
            /**
             * adding winLeft and winTop to account for dual monitor
             * using screenLeft and screenTop for IE8 and earlier
             */
            const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
            const winTop = window.screenTop ? window.screenTop : window.screenY;
            /**
             * window.innerWidth displays browser window"s height and width excluding toolbars
             * using document.documentElement.clientWidth for IE8 and earlier
             */
            const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
            const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
            const left = Math.max(0, ((width / 2) - (PopUpConstants.POPUP_WIDTH / 2)) + winLeft);
            const top = Math.max(0, ((height / 2) - (PopUpConstants.POPUP_HEIGHT / 2)) + winTop);

            authDialogRef.current = window.open(authUrl.href, `${PopUpConstants.POPUP_NAME_PREFIX}.${TokenResource.MailSend}`, `width=${PopUpConstants.POPUP_WIDTH}, height=${PopUpConstants.POPUP_HEIGHT}, top=${top}, left=${left}, scrollbars=yes`);
            if (!authDialogRef.current) {
                finishTokenRequest(false);
                return promise;
            }

            popupCloseIntervalRef.current = window.setInterval(() => {
                if (authDialogRef.current?.closed) {
                    authDialogRef.current = null;
                    finishTokenRequest(false);
                }
            }, 500);
        } catch (error) {
            logger(error, "useMailSendPopUpAuth.tsx (requestMailSendToken)");
            finishTokenRequest(false);
        }

        return promise;
    }, [finishTokenRequest]);

    const processAuthDialogResponse = React.useCallback((event: MessageEvent) => {
        if (event.origin != process.env.baseUri)
            return;

        if (!pendingRequestRef.current && !authDialogRef.current)
            return;

        let tokenReceived = false;
        try {
            if (event.data) {
                const messageFromDialog: DialogMessageResponse = event.data;
                if (messageFromDialog?.status === DialogMessageStatus.Success) {
                    if (messageFromDialog.result?.mailSendToken?.length > 0) {
                        let authState: IAuthTokenResultDto = authModule.getPersistedAuthState();
                        if (authState) {
                            authState.mailSendToken = messageFromDialog.result.mailSendToken;
                            authState.mailSendTokenExpiresOn = messageFromDialog.result.mailSendTokenExpiresOn;
                            authModule.persistAuthState(authState);
                            setIsMailSendTokenAvailable(true);
                            tokenReceived = true;
                        }
                    }
                }
            }
        } catch (error) {
            logger(error, "useMailSendPopUpAuth.tsx (processAuthDialogResponse)", event.data);
        } finally {
            closeAuthDialog();
            finishTokenRequest(tokenReceived);
        }
    }, [closeAuthDialog, finishTokenRequest]);

    React.useEffect(() => {
        isMountedRef.current = true;
        window.addEventListener("message", processAuthDialogResponse);
        return () => {
            isMountedRef.current = false;
            window.removeEventListener("message", processAuthDialogResponse);
            clearPopupCloseInterval();
            closeAuthDialog();
            pendingRequestRef.current?.resolve(false);
            pendingRequestRef.current = null;
        };
    }, [clearPopupCloseInterval, closeAuthDialog, processAuthDialogResponse]);

    React.useEffect(() => {
        if (isMailSendTokenAvailable) return undefined;

        let cancelled = false;
        (async () => {
            try {
                setIsMailSendTokenRequestInProgress(true);
                const mailSendToken: string = await authModule.getMailSendAccessToken(true);
                if (!cancelled && mailSendToken) {
                    setIsMailSendTokenAvailable(true);
                    if (pendingRequestRef.current) {
                        closeAuthDialog();
                        finishTokenRequest(true);
                    }
                }
            } catch (error) {
                logger(error, "useMailSendPopUpAuth.tsx (getMailSendAccessToken)");
            } finally {
                if (!cancelled && !pendingRequestRef.current) {
                    setIsMailSendTokenRequestInProgress(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [closeAuthDialog, finishTokenRequest, isMailSendTokenAvailable]);

    return { isMailSendTokenAvailable, isMailSendTokenRequestInProgress, requestMailSendToken }
}
